import { useEffect } from "react";

const GA_ID = "G-CMKRWFZPC4";
const STORAGE_KEY = "pixiweb_consent_v1";

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
  }
}

type ConsentState = {
  necessary: true;
  analytics: boolean;
  marketing: boolean;
  ts: number;
};

function readConsent(): ConsentState | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as ConsentState) : null;
  } catch {
    return null;
  }
}

function ensureGtagBootstrap() {
  if (typeof window === "undefined") return;
  if (typeof window.gtag === "function") return;
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer.push(arguments);
  };
  // Google Consent Mode v2 — alles standardmäßig abgelehnt (DSGVO-konform)
  window.gtag("consent", "default", {
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    analytics_storage: "denied",
    functionality_storage: "granted",
    security_storage: "granted",
    wait_for_update: 500,
  });
  window.gtag("js", new Date());
  window.gtag("config", GA_ID, {
    anonymize_ip: true,
    send_page_view: false, // erst nach Consent
  });
}

function loadGtagScript() {
  if (typeof document === "undefined") return;
  if (document.getElementById("ga4-script")) return;
  const s = document.createElement("script");
  s.id = "ga4-script";
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(s);
}

function applyConsent(state: ConsentState | null) {
  if (typeof window === "undefined" || !window.gtag) return;
  const analytics = state?.analytics === true;
  const marketing = state?.marketing === true;
  window.gtag("consent", "update", {
    analytics_storage: analytics ? "granted" : "denied",
    ad_storage: marketing ? "granted" : "denied",
    ad_user_data: marketing ? "granted" : "denied",
    ad_personalization: marketing ? "granted" : "denied",
  });
  if (analytics) {
    loadGtagScript();
    window.gtag("event", "page_view", {
      page_path: window.location.pathname + window.location.search,
      page_location: window.location.href,
      page_title: document.title,
    });
  }
}

export function GoogleAnalytics() {
  useEffect(() => {
    ensureGtagBootstrap();
    applyConsent(readConsent());

    const onConsent = (e: Event) => {
      const detail = (e as CustomEvent<ConsentState>).detail;
      applyConsent(detail ?? readConsent());
    };
    window.addEventListener("pixiweb:consent", onConsent);
    return () => window.removeEventListener("pixiweb:consent", onConsent);
  }, []);

  // SPA-Pageviews bei Routenwechsel (nur wenn Consent erteilt)
  useEffect(() => {
    let lastPath = typeof window !== "undefined" ? window.location.pathname + window.location.search : "";
    const trackIfChanged = () => {
      if (typeof window === "undefined" || !window.gtag) return;
      const state = readConsent();
      if (!state?.analytics) return;
      const current = window.location.pathname + window.location.search;
      if (current === lastPath) return;
      lastPath = current;
      window.gtag("event", "page_view", {
        page_path: current,
        page_location: window.location.href,
        page_title: document.title,
      });
    };
    const origPush = history.pushState;
    const origReplace = history.replaceState;
    history.pushState = function (...args) {
      const r = origPush.apply(this, args as Parameters<typeof origPush>);
      setTimeout(trackIfChanged, 0);
      return r;
    };
    history.replaceState = function (...args) {
      const r = origReplace.apply(this, args as Parameters<typeof origReplace>);
      setTimeout(trackIfChanged, 0);
      return r;
    };
    window.addEventListener("popstate", trackIfChanged);
    return () => {
      history.pushState = origPush;
      history.replaceState = origReplace;
      window.removeEventListener("popstate", trackIfChanged);
    };
  }, []);

  return null;
}
